import { CloseOutlined } from '@ant-design/icons'
import { Trans, useLingui } from '@lingui/react/macro'
import { Spin } from 'antd'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { TooltipSparkline } from 'features/knowledgeDocumentViewer/components/viewer/shared/TooltipSparkline'
import { useInstrumentTimeSeriesData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import { ExternalLink, LineChart } from 'lucide-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

export type SensorPreviewPlacement = 'side' | 'top'

const SIDE_PANEL_WIDTH_PX = 500

const Panel = styled.div<{ $placement: SensorPreviewPlacement }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  color: ${(props) => props.theme.forecast.defaultText};
  ${({ $placement, theme }) =>
    $placement === 'side'
      ? `
    height: 100%;
    width: ${SIDE_PANEL_WIDTH_PX}px;
    border-left: 1px solid ${theme.forecast.border};
  `
      : `
    width: 100%;
    border-bottom: 1px solid ${theme.forecast.border};
  `}
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0.75rem;
`

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`

const SparklineArea = styled.div`
  padding: 0.5rem 0.75rem;
`

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 0.75rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const NoDataIcon = styled(LineChart)`
  opacity: 0.4;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0.75rem;
`

const MetadataContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0.75rem;
`

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  padding: 1rem;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.forecast.cardGroupBackground} 50%,
    transparent
  );
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const PrimaryActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

interface SensorPreviewPanelProps {
  sensor: SensorSearchItem | null
  isOpen: boolean
  onClose: () => void
  onOpenDashboard: (sensor: SensorSearchItem) => void
  onOpenNewTab?: (sensor: SensorSearchItem) => void
  onAddToContext?: (sensor: SensorSearchItem) => void
  placement?: SensorPreviewPlacement
}

export function SensorPreviewPanel({
  sensor,
  isOpen,
  onClose,
  onOpenDashboard,
  onOpenNewTab,
  placement = 'side',
}: SensorPreviewPanelProps) {
  const { t } = useLingui()
  const sensorIds = useMemo(() => (sensor ? [sensor.sensorId] : []), [sensor])

  const { sparklineData, isLoading: isLoadingSparkline } =
    useInstrumentTimeSeriesData(sensorIds, isOpen && !!sensor)

  const sparkline = sparklineData[0] ?? null

  if (!isOpen || !sensor) {
    return null
  }

  return (
    <Panel $placement={placement}>
      <Header>
        <TitleArea>
          <Text
            as='h3'
            variant='label'
            weight='semibold'
            truncate
            title={sensor.name}>
            {sensor.name}
          </Text>
          {sensor.description && (
            <Text
              as='p'
              variant='caption'
              color='muted'
              truncate
              title={sensor.description}>
              {sensor.description}
            </Text>
          )}
        </TitleArea>
        <Button
          variant='ghost'
          size='icon-lg'
          onClick={onClose}
          aria-label={t`Close preview`}>
          <CloseOutlined />
        </Button>
      </Header>

      {isLoadingSparkline ? (
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      ) : sparkline ? (
        <SparklineArea>
          <TooltipSparkline
            piTag={sparkline.piTag}
            data={sparkline.data}
            currentValue={sparkline.currentValue}
            previousValue={sparkline.previousValue}
            lastMeasuredAt={sparkline.lastMeasuredAt}
            unit={sparkline.unit}
          />
        </SparklineArea>
      ) : (
        <NoDataContainer>
          <NoDataIcon size={32} />
          <Text as='p' size='sm'>
            <Trans>No recent data</Trans>
          </Text>
        </NoDataContainer>
      )}

      <MetadataContainer>
        <MetadataItem>
          <Text as='p' variant='caption' color='muted' leading='tight'>
            <Trans>Unit</Trans>
          </Text>
          <Text as='p' size='sm'>
            {sensor.unit || '-'}
          </Text>
        </MetadataItem>
        <MetadataItem>
          <Text as='p' variant='caption' color='muted' leading='tight'>
            <Trans>Asset</Trans>
          </Text>
          <Text as='p' size='sm'>
            {sensor.asset || '-'}
          </Text>
        </MetadataItem>
      </MetadataContainer>

      <ActionButtons>
        <PrimaryActions>
          <Button
            variant='primaryRaised'
            size='lg'
            onClick={() => onOpenDashboard(sensor)}>
            <Trans>Open</Trans>
          </Button>
          {onOpenNewTab && (
            <Button
              variant='inputRaised'
              size='lg'
              onClick={() => onOpenNewTab(sensor)}>
              <Trans>Open In New Tab</Trans>
              <ExternalLink />
            </Button>
          )}
        </PrimaryActions>
      </ActionButtons>
    </Panel>
  )
}
